import { useState } from 'react';

import { useAbilityContext } from '#state/contexts/ability-context.js';
import { EvidencePanelToolbar } from './evidence-panel-toolbar.js';
import { useEvidenceRowSelectionStore } from './evidence-row-selection-store.js';
import { SelectedRowEvidence } from './selected-row-evidence.js';
import { ShowInDatasheetContext } from './show-in-datasheet-context.js';

import type { ShowInDatasheet } from './show-in-datasheet-context.js';

/** Props for {@link DatasheetWindowEvidence}. */
export interface DatasheetWindowEvidenceProps {
	/** File-store identifier of the datasheet the window shows. */
	fileId: string;
	/** The package whose evidence the window was opened from. */
	packageId: string;
	/** Shows a locator in the window's own datasheet. */
	onShowInDatasheet: ShowInDatasheet;
}

/**
 * The evidence for the viewer's pick of the package's rows, as a datasheet window shows it beside the datasheet: the
 * switches that have datasheet windows follow the pick and reveal rejected locators, above the pick's evidence, or a
 * prompt to pick a row when no row of the package is picked. Opening a locator shows it in the window's own datasheet
 * rather than another. Vote buttons appear only for viewers with wizard access.
 */
export function DatasheetWindowEvidence({ fileId, packageId, onShowInDatasheet }: DatasheetWindowEvidenceProps) {
	const [showRejected, setShowRejected] = useState(false);
	const canVote = useAbilityContext().can('manage', 'wizard');
	const rowSelection = useEvidenceRowSelectionStore((state) => state.selection);
	const clearRowSelection = useEvidenceRowSelectionStore((state) => state.clear);
	const followSelection = useEvidenceRowSelectionStore((state) => state.followSelection);
	const setFollowSelection = useEvidenceRowSelectionStore((state) => state.setFollowSelection);

	return (
		<ShowInDatasheetContext value={onShowInDatasheet}>
			<div className='flex min-h-0 grow flex-col overflow-auto'>
				<EvidencePanelToolbar
					showRejected={showRejected}
					onShowRejectedChange={setShowRejected}
					followSelection={followSelection}
					onFollowSelectionChange={setFollowSelection}
				/>
				<div className='flex grow flex-col gap-3 p-3'>
					{rowSelection !== null && rowSelection.packageId === packageId ?
						<SelectedRowEvidence
							fileId={fileId}
							selection={rowSelection}
							canVote={canVote}
							showRejected={showRejected}
							onClear={clearRowSelection}
							clickShowsInDatasheet
						/>
					:	<p className='text-gray-a9 italic'>Select a row in the editor to see its evidence here.</p>}
				</div>
			</div>
		</ShowInDatasheetContext>
	);
}
